import React from 'react'
import classNames from 'classnames'
import { Link, LinkProps, NavLinkProps } from 'react-router-dom'

import { createStyles, withStyles, WithStyles, Theme } from '@material-ui/core/styles'

import Typography from '@material-ui/core/Typography'
import { SvgIconProps } from '@material-ui/core/SvgIcon'
import ButtonBase, { ButtonBaseProps } from '@material-ui/core/ButtonBase'

interface Props extends ButtonBaseProps {
  to?: LinkProps['to']
  icon?: React.ComponentType<SvgIconProps>,
  label?: React.ReactNode,
  color?: 'dark' | 'light' | 'neutral',
  clipped?: boolean,
}

const styles = (theme: Theme) => {
  const clipPath = `
    polygon(
      0% ${theme.notchSize},
      ${theme.notchSize} 0%,
      calc(100% - ${theme.notchSize}) 0%,
      100% ${theme.notchSize},
      100% 100%,
      0% 100%
    )
  `

  return createStyles({
    root: {
      color: theme.palette.common.white,
      width: '100%',
      height: 44,
      fontWeight: 700,
      background: 'linear-gradient(187.66deg, #07F7A9 -0.01%, #00E89C 99.77%)',
      borderBottomLeftRadius: 2,
      borderBottomRightRadius: 2,
      '&$neutral': {
        background: `linear-gradient(187.66deg, ${theme.palette.grey[50]} -0.01%, ${theme.palette.grey[100]} 99.77%)`,
      },
    },
    title: {
      color: theme.palette.common.white,
      fontWeight: 700,
    },
    dark: {
      color: theme.palette.primary.contrastText,
      fontWeight: 600,
    },
    neutral: {
      color: theme.palette.primary.contrastText,
      fontWeight: 600,
    },
    icon: {
      marginRight: theme.spacing(),
    },
    clipped: {
      clipPath,
      '-webkit-clip-path': clipPath,
    },
    disabled: {
      '&$neutral': {
        color: theme.palette.grey[600],
      },
    }
  })
}

type InjectedProps = Props & WithStyles<typeof styles>

const GameCardButton: React.SFC<InjectedProps> = ({
  to,
  icon: Icon,
  color,
  label,
  clipped,
  classes,
  disabled,
  ...props
}) => {
  const isDark = color === 'dark'
  const isNeutral = color === 'neutral'

  // TODO: translation key needed

  return (
    <ButtonBase
      classes={{ root: classes.root }}
      disabled={disabled}
      className={classNames({ [classes.dark]: isDark, [classes.neutral]: isNeutral, [classes.clipped]: clipped })}
      component={to ? ({ innerRef, ...buttonProps }) => <Link {...buttonProps as NavLinkProps} to={to} /> : 'button'}
      {...props}
    >
      {Icon && <Icon className={classes.icon} />}
      <Typography
        className={classNames(classes.title, {
          [classes.dark]: isDark,
          [classes.neutral]: isNeutral,
          [classes.disabled]: disabled,
        })}
      >
        {label}
      </Typography>
    </ButtonBase>
  )
}

GameCardButton.defaultProps = {
  color: 'light',
  label: 'Accept Challenge',
}

export default withStyles(styles)(GameCardButton)
